import styled from "@emotion/styled";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  Chip,
  FieldSelect,
  Form,
  FormikForm,
  FormRowField,
  MainPanel,
  MainPanelDraggableItem,
  PopOutPanel,
  Skeleton,
  useSidebar,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import { contentLanguagesSelector, primaryContentLanguageCodeSelector } from "@skilitics/nautilus-core-designer-data";

import { activeContentLanguageCodeSelector } from "../state";
import type { AddNewLanguageFormValues } from "./AddContentLanguage";
import { AddContentLanguage } from "./AddContentLanguage";
import { useAddContentLanguage, useRemoveContentLanguage, useUpdateContentLanguage } from "./contentLanguages";
import { EditContentLanguage } from "./EditContentLanguage";
import { useAvailableLanguages } from "./useAvailableLanguages.ts";

const Item = styled(MainPanelDraggableItem)`
  border-bottom: solid 1px ${({ theme }) => theme.outlineSoft};
`;

const ItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding: 0 0 0 12px;
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Name = styled.div``;

const Precisions = styled.div`
  opacity: 0.5;

  font-size: ${({ theme }) => theme.fontSizeSmall};
`;

type PrimaryLanguageFormValues = {
  primaryLanguageCode: string;
};

// Some Business Logic to keep note of:
// - Primary language can't have a "base".
// - Active language should never be set to a language not supported by the current module.

export const LanguagePanel = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const { popOut } = useSidebar();
  const languages = useAvailableLanguages();
  const contentLanguages = useSelector(contentLanguagesSelector);
  const primaryLanguageCode = useSelector(primaryContentLanguageCodeSelector);
  const activeLanguageCode = useSelector(activeContentLanguageCodeSelector);

  const { addLanguage, isAdding } = useAddContentLanguage();
  const { removeLanguage } = useRemoveContentLanguage();
  const { updateLanguage, isUpdating } = useUpdateContentLanguage();

  const isDisabled = isAdding || isUpdating;

  const noneOrPrimaryLanguageCodeToNull = useMemo(
    () =>
      ({ baseCode, code }: AddNewLanguageFormValues) =>
        baseCode === "none" ||
        (contentLanguages.find(({ language }) => language.code === code) ?? "") === primaryLanguageCode
          ? null
          : (baseCode ?? null),
    [contentLanguages, primaryLanguageCode],
  );

  const handleSubmitAdd = useCallback(
    async (values: AddNewLanguageFormValues) => {
      await addLanguage({
        code: values.code,
        baseCode: noneOrPrimaryLanguageCodeToNull(values),
      });
      popOut.closePanel();
    },
    [addLanguage, noneOrPrimaryLanguageCodeToNull, popOut],
  );

  const handleSubmitEdit = useCallback(
    async (values: AddNewLanguageFormValues) => {
      await updateLanguage({
        code: values.code,
        baseCode: noneOrPrimaryLanguageCodeToNull(values),
      });
      popOut.closePanel();
    },
    [updateLanguage, noneOrPrimaryLanguageCodeToNull, popOut],
  );

  const handleSubmitPrimaryLanguage = (_values: PrimaryLanguageFormValues) => {
    console.warn("changing primary language not supported yet (but not hard)");
  };

  const handleRemove = (code: string) => {
    removeLanguage(code);
    popOut.closePanel();
  };

  const handleAdd = () => {
    popOut.setPanelDangerously(
      <PopOutPanel
        key={contentLanguages.length || "add"}
        heading={formatMessage("addLanguage")}
        description={formatMessage("addLanguageDescription")}
        contextActions={undefined}
      >
        <AddContentLanguage
          availableLanguages={languages}
          contentLanguages={contentLanguages}
          onCancel={() => popOut.closePanel()}
          onSubmit={handleSubmitAdd}
        />
      </PopOutPanel>,
    );
    popOut.openPanel();
  };

  const handleContentLanguageSelect = (selectedCode: string) => {
    const contentLanguage = contentLanguages.find(({ code }) => code === selectedCode);
    if (!contentLanguage) {
      return;
    }
    popOut.setPanelDangerously(
      <PopOutPanel
        key={selectedCode}
        heading={formatMessage("editLanguage")}
        description={formatMessage("editLanguageDescription")}
        contextActions={undefined}
      >
        <EditContentLanguage
          activeLanguage={activeLanguageCode}
          availableLanguages={languages}
          contentLanguage={contentLanguage}
          contentLanguages={contentLanguages}
          onCancel={() => popOut.closePanel()}
          onRemove={handleRemove}
          onSubmit={handleSubmitEdit}
          primaryLanguageCode={primaryLanguageCode}
        />
      </PopOutPanel>,
    );
    popOut.openPanel();
  };

  const moduleLanguages = [
    ...contentLanguages.map(({ baseCode, language: { code, title } }) => {
      const baseTitle = (baseCode && contentLanguages.find((cl) => baseCode === cl.code)?.language.title) ?? "";
      return (
        <Item
          key={code}
          label={
            <ItemContainer className={isDisabled ? "disabled" : ""}>
              <Details>
                <Name data-test="language-label">{title}</Name>
                {/* TODO: Display language metadata such as Direction: LTR/RTL, Spellchecker dictionary etc. */}
                <Precisions>{baseCode && `Falls back to ${baseTitle}`}</Precisions>
              </Details>
              {code === activeLanguageCode && <Chip label={formatMessage("activeLanguageLabel")} size="small" />}
            </ItemContainer>
          }
          active={false}
          draggable={false}
          onSelect={() => handleContentLanguageSelect(code)}
        ></Item>
      );
    }),
    ...(isAdding ? [<Skeleton key="loading" height={50} />] : []),
  ];

  const primaryLanguageOptions = [
    ...contentLanguages
      .filter(({ baseCode }) => !baseCode)
      .map(({ language: { code, title } }) => ({
        label: `${code} - ${title}`,
        value: code,
      })),
    {
      value: "",
      label: formatMessage("primaryLanguageNote"),
      disabled: true,
    },
  ];

  return (
    <>
      <MainPanel title={formatMessage("primaryLanguage")} description={formatMessage("primaryLanguageSummary")}>
        <FormikForm<PrimaryLanguageFormValues>
          initialValues={{ primaryLanguageCode }}
          onSubmit={handleSubmitPrimaryLanguage}
          formatStrings={formatStrings}
          submitOnBlur
          dataTest="languages-form"
        >
          <Form style={{ padding: "0 16px" }}>
            <FormRowField<PrimaryLanguageFormValues> name="primaryLanguageCode">
              <FieldSelect
                options={primaryLanguageOptions}
                // TODO: Add the ability to change primary language, JIRA: IN-562
                // disabled={isDisabled}
                disabled={true}
                menuItemStyles={{ whiteSpace: "normal" }}
              />
            </FormRowField>
          </Form>
        </FormikForm>
      </MainPanel>

      <MainPanel
        title={formatMessage("supportedLanguages")}
        description={formatMessage("supportedLanguagesDescription")}
        addButton={{ label: formatMessage("addLanguage"), position: "after", onAdd: handleAdd }}
        isFetching={isDisabled}
      >
        {moduleLanguages}
      </MainPanel>
    </>
  );
};

const formatStrings = {
  addLanguage: {
    en: "Support Additional Language",
  },
  addLanguageDescription: {
    en: "Add a language you require this module to support.",
  },
  editLanguage: {
    en: "Modify Language",
  },
  editLanguageDescription: {
    en: "View details about the supported language.",
  },
  activeLanguageLabel: {
    en: "Active",
  },
  primaryLanguageCodeLabel: {
    en: "Primary Language",
  },
  primaryLanguageNote: {
    en: "Note: Primary Language cannot be set to a language that already has a base language associated.",
  },
  // todo: avoiding automatic description...
  primaryLanguage: {
    en: "Primary Language",
  },
  primaryLanguageSummary: {
    en: "This is the primary language of your content, and the language all translations will be based on. Note: This cannot be changed after module creation.",
  },
  supportedLanguages: {
    en: "Supported Languages",
  },
  supportedLanguagesDescription: {
    en: "This is a list of other languages you require this module to support natively.",
  },
};
